/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  createDialogRoute,
  DateTimeTypes,
  DictionaryService,
  FormState,
  FormStateBuilder,
  getDialogInitData,
  IS_REGION_AU_OR_NZ,
  whenCreateOrEditMode,
} from '@gh/common-shared';
import {
  AutocompleteLoadMoreEvent,
  DialogInput,
  DialogService,
  FormMode,
  NotificationService,
  responsiveDialogOverlayPaneClass,
  WaitingService,
} from '@gh/core-ui';
import {
  addMinutes,
  addMonths,
  currentDate,
  isDateValid,
  mergeDateAndTime,
  normalizeDate,
  setHours,
  setMinutes, UTCToLocal,
} from '@gh/core-util';
import { omit } from 'lodash';
import {
  CommunicationsBusinessService,
  MeetingDialogConfig,
} from '../../resources/communications-business.service';
import { CommunicationEntryPoint } from '../../resources/communications.model';
import { BehaviorSubject } from 'rxjs';
import { map$ } from '@gh/rx/operators';
import { TranslationService } from '@gh/core-mls';
import {
  ActivityDetails,
  ActivityRestService, Assignee,
  GuestListItemDto,
  LeadRestService,
  SalesFolderShortDto,
} from '@gh/builder-shared-data';
import { BuilderUserContext } from '../../../user';

export type ActivityDetailsWithTime = ActivityDetails & { startTime: Date; endTime: Date; guest: any };

@Component({
  standalone: false,
  selector: 'gh-meeting-dialog',
  templateUrl: 'meeting-dialog.component.html',
  styleUrls: ['meeting-dialog.component.scss'],
})
export class MeetingDialogComponent implements OnInit, DialogInput<MeetingDialogConfig> {
  input: MeetingDialogConfig;
  form: FormState<ActivityDetailsWithTime>;
  communicationEntryPoint = CommunicationEntryPoint;
  // according to the UC should not be possible to set the date less then current date minus 12 months.
  minAllowedDate = addMonths(normalizeDate(currentDate()), -12);

  incrementDefaultMinutes = 30;
  endTime = { hours: 23, minutes: 59 };
  startDate = new BehaviorSubject<Date>(new Date());
  startDate$ = this.startDate.asObservable();
  endDate = new BehaviorSubject<Date>(new Date());
  endDate$ = this.endDate.asObservable();
  isDateInvalid: boolean;
  activityTypes: any[];
  salesFolderList: SalesFolderShortDto[] = [];
  maximumLength = 4000;
  descriptionsLength = 0;
  notesLength = 0;
  preventAutocompleteOpen = true;

  get isFormEditable(): boolean {
    return this.form.mode !== FormMode.ViewMode;
  }
  private get getStartDate(): Date {
    return this.startDate.getValue();
  }
  private get getEndDate(): Date {
    return this.endDate.getValue();
  }

  constructor(private dialogRef: MatDialogRef<any>,
              @Inject(MAT_DIALOG_DATA) private dialogData: any,
              private business: CommunicationsBusinessService,
              private userContext: BuilderUserContext,
              private leadService: LeadRestService,
              private activityService: ActivityRestService,
              private waitingService: WaitingService,
              private dictionaryService: DictionaryService,
              private notifications: NotificationService,
              private translate: TranslationService,
              private dialogService: DialogService,
              private formStateBuilder: FormStateBuilder) {
  }

  get title(): string {
    return {
      [FormMode.CreateMode]: 'lbl_activity_new',
      [FormMode.EditMode]: 'lbl_activity_edit',
      [FormMode.ViewMode]: 'lbl_activity_view',
    }[this.form.mode];
  }

  ngOnInit(): void {
    const { mode, salesFolderId, entryPoint, contactTypeCode } = this.input;
    const { meeting, salesFolderList, activityTypes, assignee } = getDialogInitData(this.dialogData);
    this.activityTypes = activityTypes;
    this.salesFolderList = salesFolderList;
    const fixedStartDate =  UTCToLocal(meeting.startDate);
    const fixedEndDate = meeting.endDate ?  UTCToLocal(meeting.endDate) : addMinutes(fixedStartDate, 30);
    this.setStartDate(fixedStartDate);
    this.setEndDate(fixedEndDate);
    this.form = this.formStateBuilder.buildPlain({
      value: {
        ...omit(meeting, 'startTime', 'startDate', 'endTime', 'endDate', 'guest'),
        startDate: fixedStartDate,
        startTime: fixedStartDate,
        endDate: fixedEndDate,
        endTime: fixedEndDate,
        activityType: meeting.activityType ? meeting.activityType.toString() : '',
        guests: meeting.guests || [],
        guest: '',
        salesFolderId,
        contactTypeCode,
        assignee: meeting.assignee || assignee,
      },
      mode,
      enabled: whenCreateOrEditMode(),
      children: {
        id: void 0,
        meetingId: void 0,
        activityType: { required: true },
        description: void 0,
        isDone: void 0,
        subject: { required: true },
        active: void 0,
        salesFolderId: {
          required: entryPoint === CommunicationEntryPoint.LeadList || entryPoint === CommunicationEntryPoint.LeadCommunication,
          visible: entryPoint === CommunicationEntryPoint.LeadList || entryPoint === CommunicationEntryPoint.LeadCommunication,
        },
        notes: void 0,
        location: void 0,
        startDate: { required: true, type: DateTimeTypes.ShortDate },
        endDate: { required: true, type: DateTimeTypes.ShortDate },
        startTime: { required: true, type: DateTimeTypes.ShortTime },
        endTime: { required: true, type: DateTimeTypes.ShortTime },
        allDay: void 0,
        assignee: { required: true },
        guests: void 0,
        guest: void 0,
        contactTypeCode: void 0,
        version: void 0,
      },
    });
    this.form.getControl('startTime').valueChanges.subscribe((time) => {
      if (!!time && isDateValid(time)) {
        this.onStartTimeChange(time);
      }
    });
    this.form.getControl('endTime').valueChanges.subscribe((time) => {
      if (!!time && isDateValid(time)) {
        this.onEndTimeChange(time);
      }
    });
    this.form.getControl('startDate').valueChanges.subscribe(() => {
      this.dateCheck();
    });
    this.form.getControl('endDate').valueChanges.subscribe(() => {
      this.dateCheck();
    });
    this.startDate$.subscribe(() => this.dateCheck());
    this.endDate$.subscribe(() => this.dateCheck());
    if (meeting.allDay) {
      this.onAllDayChange({checked: true});
    }
    if (this.form.viewMode) {
      this.form.get('guest')?.disable();
    }
  }

  dateCheck(): void {
    const endDate = this.getEndDate;
    const startDate = this.getStartDate;

    const arrayControls =  [
      this.form.getControl('endDate'),
      this.form.getControl('startDate'),
      this.form.getControl('startTime'),
      this.form.getControl('endTime'),
    ];
    this.isDateInvalid = isDateValid(endDate) && isDateValid(startDate) && endDate < startDate
      && !arrayControls.find((item) => item.invalid);
    if (this.isDateInvalid) {
      setTimeout(() => {
        arrayControls.forEach((control) => {
          control.markAsTouched();
          control.setErrors({});
        });
      },         0);
      return;
    }

    arrayControls.forEach((control) => {
      const value = control.value;
      if (value && isDateValid(value)) {
        control.setErrors(null);
      }
    });

  }
  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (this.form.group.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value: ActivityDetailsWithTime = this.form.value;
    this.business.saveMeeting(this.input.entryPoint, {...this.input, salesFolderId: value.salesFolderId}, {
      ...<ActivityDetails>omit(value, 'startDate', 'endDate', 'startTime', 'endTime', 'guest'),
      startDate: this.getStartDate,
      endDate: this.getEndDate,
    }).pipe(this.waitingService.operator())
      .subscribe(
        (meetingList) => {
          this.notifications.success(this.form.createMode ? 'msg_created_successfully' : 'msg_saved_successfully');
          this.dialogRef.close(meetingList);
        },
        this.notifications.processAll);
  }

  onAllDayChange({ checked }: any): void {
    const endTimeControl = this.form.getControl('endTime');
    const startTimeControl = this.form.getControl('startTime');
    if (checked) {
      const endTime = endTimeControl.value;
      endTimeControl.setValue(setMinutes(setHours(endTime, this.endTime.hours), this.endTime.minutes));
      endTimeControl.disable();

      const startTime = startTimeControl.value;
      startTimeControl.setValue(setMinutes(setHours(startTime, 0), 0));
      startTimeControl.disable();
      return;
    }
    const currentTime = new Date();
    const startDateTime = mergeDateAndTime(this.form.getControl('startDate').value, currentTime);
    this.setStartDate(startDateTime);
    startTimeControl.setValue(startDateTime);
    startTimeControl.enable();

    const endDateTime = addMinutes(mergeDateAndTime(this.form.getControl('endDate').value, new Date()), this.incrementDefaultMinutes);
    this.setEndDate(endDateTime);
    endTimeControl.setValue(endDateTime);
    endTimeControl.enable();
  }

  onStartDateChange({ value }: any): void {
    const time = this.form.getControl('startTime').value;
    if (!isDateValid(value) || !isDateValid(time)) {
      return;
    }
    this.setStartDate(mergeDateAndTime(value, this.form.getControl('startTime').value));
    this.form.getControl('startTime').setValue(this.getStartDate);
    this.recalculateEndDateTime();
  }

  onEndDateChange({ value }: any): void {
    const time = this.form.getControl('endTime').value;
    if (!isDateValid(value) || !isDateValid(time)) {
      return;
    }
    this.setEndDate(mergeDateAndTime(value, this.form.getControl('endTime').value));
    this.form.getControl('endTime').setValue(this.getEndDate);
  }

  onStartTimeChange(startTime: Date): void {
    this.setStartDate(mergeDateAndTime(this.form.getControl('startDate').value, startTime));
    this.form.getControl('startDate').setValue(this.getStartDate);
    this.recalculateEndDateTime();
  }

  onEndTimeChange(endTime: Date): void {
    this.setEndDate(mergeDateAndTime(this.form.getControl('endDate').value, endTime));
    this.form.getControl('endDate').setValue(this.getEndDate);
  }

  loadCustomers($event: AutocompleteLoadMoreEvent): void {
    const { query, load } = $event;
    const entryPoint = this.input.entryPoint;
    const contactId = entryPoint === CommunicationEntryPoint.Contact
    || entryPoint === CommunicationEntryPoint.Job ? this.input.contactId : undefined;
    load(this.business.findGuestsByName(this.form.getControl('salesFolderId').value, query, contactId).pipe(map$((res) => {
      const selectedGuests = this.form.getControl('guests')?.value?.map((contact: any) => contact.contactId);
      return res.filter((item) => !selectedGuests.includes(item.id))
        .map((item) => ({...item, displayName: this.getGuestInfo(item)}));
    })));
  }

  loadAssignee($event: AutocompleteLoadMoreEvent): void {
    const { query, load } = $event;

    load(this.business.findAssigneeByName(query));
  }

  deleteGuest(guest: any): void {
    if (!this.isFormEditable) {
      return;
    }
    const control = this.form.get('guests');
    if (!control) {
      return;
    }
    const idField = guest.contactId ? 'contactId' : 'id';
    const currentList = control.value || [];
    control.setValue(currentList.filter((item: any) => item[idField] !== guest[idField]));
  }

  onSelectGuest(guest: any): void {
    const control = this.form.get('guests');
    if (guest) {
      control.setValue([...control.value, {...omit(guest, 'displayName')}]);
      this.form.get('guest')?.setValue('', { emitEvent: false });
    }
  }

  getGuestInfo(guest: GuestListItemDto | Assignee): string {
    return `${guest.contactName} (${guest.email ? guest.email : this.translate.get('lbl_no_email')})`;
  }

  getFormattedDate(timeStamp: Date): string {
    const date = new Date(timeStamp);
    if (IS_REGION_AU_OR_NZ) {
      return `${ date.getDate() }/${ date.getMonth() + 1 }/${ date.getFullYear() }`;
    }
    return `${ date.getMonth() + 1 }/${ date.getDate() }/${ date.getFullYear() }`;
  }

  private setStartDate(value: Date): void {
    this.startDate.next(value);
  }

  private setEndDate(value: Date): void {
    this.endDate.next(value);
  }

  private recalculateEndDateTime(): void {
    const startDate = this.getStartDate;
    const endDate = this.getEndDate;
    if (endDate < startDate) {
      const newEndDate =  addMinutes(startDate, 30);
      this.form.getControl('endTime').setValue(newEndDate);
      this.form.getControl('endDate').setValue(newEndDate);
      this.setEndDate(newEndDate);
      setTimeout(() => this.dateCheck(), 0);
    }
  }
  onDescriptionChange(event: Event): void {
    const input = event.target as HTMLTextAreaElement;
    this.descriptionsLength = input.value.length;
  }

  onNotesChange(event: Event): void {
    const input = event.target as HTMLTextAreaElement;
    this.notesLength = input.value.length;
  }
}

export const meetingDialogRoute = createDialogRoute({
  component: MeetingDialogComponent,
  init: {
    service: CommunicationsBusinessService,
    selector: 'initMeetingDialogView',
  },
  config: {
    panelClass: responsiveDialogOverlayPaneClass(),
    autoFocus: false,
  },
});
